import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Heart, Sun, Sparkles } from 'lucide-react'
import { getAllPosts } from '../lib/content.js'
import siteSettings from '../content/settings/site.json'
import ImageCarousel from '../components/ImageCarousel'

function CloudVillage() {
  const [featuredPosts, setFeaturedPosts] = useState([])

  useEffect(() => {
    async function loadPosts() {
      const data = await getAllPosts()
      // Get first 3 posts
      setFeaturedPosts(data.slice(0, 3))
    }
    loadPosts()
  }, [])

  const stats = siteSettings.stats || []

  return (
    <div>
      {/* Hero Section - Light Theme */}
      <section className="relative bg-gradient-to-br from-white via-orange-50 to-green-50 min-h-screen flex items-center pt-16 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-troh-primary/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-troh-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-troh-primary/10 backdrop-blur-sm mb-8">
              <Sun className="w-4 h-4 text-troh-primary" />
              <span className="text-sm text-troh-dark">"Hope is being able to see that there is light despite all of the darkness." — Desmond Tutu</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-troh-dark mb-6 tracking-tight">
              {siteSettings.title}
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-6 max-w-3xl mx-auto">
              {siteSettings.description}
            </p>
            <blockquote className="text-lg text-gray-500 mb-10 max-w-2xl mx-auto italic">
              "Even in the face of no cure, there is still courage. You are not your diagnosis—you are strength, light, and the living proof that hope refuses to fade."
            </blockquote>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/about"
                className="inline-flex items-center justify-center px-8 py-4 bg-troh-primary hover:bg-red-500 text-white rounded-full font-medium transition-all hover:scale-105 hover:shadow-lg hover:shadow-troh-primary/25"
              >
                Our Story
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/colors"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-troh-primary text-troh-primary hover:bg-troh-primary hover:text-white rounded-full font-medium transition-all hover:scale-105"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Colors of Cancer
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <span className="text-troh-primary font-semibold text-sm uppercase tracking-wider">Who We Are</span>
              <h2 className="text-3xl md:text-4xl font-bold text-troh-dark mt-2 mb-6">A Safe Harbor with Strong Walls</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Tayloring Rays of Hope is built on a simple promise: <strong>no one fights alone.</strong> We are a community of survivors, caregivers, and allies who understand that a diagnosis changes everything—but it doesn't have to steal your dignity or hope.
                </p>
                <p>
                  Elaine knows what it means to need a ride to chemo, to worry about a co-pay, to sit in a chair and wonder how you'll make it through. She's been there. And now she's here—for anyone who needs someone who <em>gets it</em>.
                </p>
                <p>
                  This is a place to exhale. To be fragile without judgment. But make no mistake—we are also vigilant. We fight bureaucracy, navigate insurance mazes, and stand in the gap when the system fails.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-troh-primary/10 to-troh-secondary/20 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-troh-dark mb-6">How We Help</h3>
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="text-troh-primary font-bold">→</span>
                  <span><strong>Transportation:</strong> Getting to treatment shouldn't be a barrier. We drive patients to chemo, radiation, and appointments—no questions asked.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-troh-primary font-bold">→</span>
                  <span><strong>Financial Support:</strong> Co-pays, prescriptions, groceries, gas cards. We fill the gaps so treatment isn't a choice between health and hunger.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-troh-primary font-bold">→</span>
                  <span><strong>Essential Needs:</strong> Warm coats for the infusion center. Toiletries for those who've forgotten their own. Dignity in small things.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-troh-primary font-bold">→</span>
                  <span><strong>Community:</strong> A listening ear at 2 AM. Someone who answers the phone and doesn't rush you. Connection that heals.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Image Gallery & Carousel */}
      <ImageCarousel />

      {/* Impact Numbers - Light Background */}
      <section className="py-16 bg-gradient-to-r from-orange-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-sm">
                <p className="text-5xl font-bold text-troh-primary mb-2">{stat.number}</p>
                <p className="text-lg text-troh-dark">{stat.label}</p>
                {stat.sublabel && (
                  <p className="text-sm text-gray-500 mt-1">{stat.sublabel}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shop for Reparations - Coral Background */}
      <section className="py-24 bg-troh-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Support Through Our Shop</h2>
          <p className="text-xl leading-relaxed mb-8">
            Every purchase directly supports families facing life-changing diagnoses—covering co-pays, transportation, food, and basic necessities that insurance doesn't reach.
          </p>
          <p className="text-lg opacity-90 mb-8">
            100% of proceeds go toward helping real families in our community.
          </p>
          <a
            href={`https://${import.meta.env.VITE_SHOPIFY_STORE_DOMAIN}`}
            className="inline-flex items-center justify-center px-8 py-4 bg-white text-troh-primary rounded-full font-semibold transition-all hover:scale-105 hover:shadow-lg"
          >
            <Heart className="w-5 h-5 mr-2" />
            Visit Our Shop
          </a>
          <p className="text-sm mt-4 opacity-75">{siteSettings.title} is a registered 501(c)(3) nonprofit. EIN available upon request.</p>
        </div>
      </section>

      {/* Blog Preview - if posts exist */}
      {featuredPosts.length > 0 && (
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-troh-primary font-semibold text-sm uppercase tracking-wider">Journey</span>
              <h2 className="text-3xl md:text-5xl font-bold text-troh-dark mb-4 mt-2">Latest Updates</h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {featuredPosts.map((post) => (
                <article key={post.slug} className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <Link to={`/blog/${post.slug}`}>
                    <div className="relative overflow-hidden">
                      {post.image ? (
                        <img
                          src={post.image}
                          alt={post.title}
                          className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src = '/images/uploads/placeholder.jpg'
                          }}
                        />
                      ) : (
                        <div className="w-full h-56 bg-gradient-to-br from-troh-primary/20 to-troh-secondary/30 flex items-center justify-center">
                          <Sun className="w-12 h-12 text-troh-primary" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <span className="text-sm text-gray-500">{new Date(post.date).toLocaleDateString()}</span>
                      <h3 className="text-xl font-semibold text-troh-dark group-hover:text-troh-primary transition-colors mt-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mt-2 line-clamp-2">{post.excerpt}</p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/blog"
                className="inline-flex items-center px-6 py-3 border-2 border-troh-primary text-troh-primary font-medium rounded-full hover:bg-troh-primary hover:text-white transition-all"
              >
                View All Journeys
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

export default CloudVillage
